/**
 * Section « Avances à rembourser » (décision 54) : les factures payées de sa
 * poche par une personne, regroupées par personne avec leur total ; on coche
 * celles qu'un virement a remboursées et on note la date. Les remboursements
 * des 90 derniers jours restent visibles. Rien n'est écrit dans Zoho.
 */

import { dateFr, eurosCentimes } from '@scc/core';
import { h, vider } from '../../dom.js';
import { lireAvances, noterRemboursement, urlFichierPiece, type Avances, type AvancesPersonne, type Piece } from './api.js';
import { brut, creerRetour, creerSection, deNom, messageDe, table, verrouille, type Contexte, type Section } from './communs.js';
import { ETATS_PIECE } from './pieces-textes.js';

const aujourdhui = (): string => new Date().toISOString().slice(0, 10);

const montantPiece = (p: Piece): string => {
  const m = p.champs?.montant.valeur ?? null;
  const d = p.champs?.devise.valeur ?? 'EUR';
  if (m === null) return 'montant non lu';
  return d.toUpperCase() === 'EUR' ? eurosCentimes(m) : `${m.toFixed(2).replace('.', ',')} ${d}`;
};

function lignePiece(p: Piece, coche: HTMLInputElement | null): HTMLElement {
  return h(
    'tr',
    {},
    h('td', {}, coche ?? brut(p.avanceRembourseeLe === null ? '' : dateFr(p.avanceRembourseeLe))),
    h('td', { classe: 'date' }, p.champs?.date.valeur ? dateFr(p.champs.date.valeur) : '—'),
    h(
      'td',
      {},
      h('strong', {}, brut(p.champs?.fournisseur.valeur ?? 'Fournisseur non lu')),
      h('div', { classe: 'note-lecture' }, brut(`${p.nomPropose ?? p.nomOrigine} · ${ETATS_PIECE[p.etat].toLowerCase()} · `), h('a', { href: urlFichierPiece(p.id), target: '_blank', rel: 'noopener noreferrer' }, 'Ouvrir ↗')),
    ),
    h('td', { classe: 'num' }, montantPiece(p)),
  );
}

function blocPersonne(a: AvancesPersonne, ctx: Contexte, recharger: () => void): HTMLElement {
  const retour = creerRetour();
  const coches = new Map<number, HTMLInputElement>();
  for (const p of a.pieces) {
    const c = h('input', { type: 'checkbox', 'aria-label': `Remboursée : pièce ${String(p.id)}` });
    c.checked = true;
    coches.set(p.id, c);
  }
  const le = h('input', { type: 'date', value: aujourdhui(), 'aria-label': 'Date du remboursement' });
  const bouton = h('button', { type: 'button', classe: 'btn-action principal' }, 'Noter le remboursement');
  bouton.addEventListener('click', () => {
    const ids = [...coches.entries()].filter(([, c]) => c.checked).map(([id]) => id);
    if (ids.length === 0) {
      retour.ko('Cochez les factures que le virement a remboursées.');
      return;
    }
    if (le.value === '') {
      retour.ko('La date du remboursement est obligatoire.');
      return;
    }
    void (async () => {
      try {
        const r = await verrouille([bouton, le, ...coches.values()], () => noterRemboursement(ids, le.value));
        ctx.retour.ok(`${String(r.rembourses)} avance${r.rembourses > 1 ? 's' : ''} ${deNom(a.nom)} notée${r.rembourses > 1 ? 's' : ''} remboursée${r.rembourses > 1 ? 's' : ''} le ${dateFr(le.value)}.`);
        recharger();
      } catch (e) {
        retour.ko(messageDe(e, 'Le remboursement n’a pas pu être noté.'));
      }
    })();
  });
  return h(
    'div',
    { classe: 'bloc-avances' },
    h(
      'h3',
      {},
      brut(`${a.nom} · ${eurosCentimes(a.total)} à rembourser`),
      a.sansMontant > 0 ? h('span', { classe: 'note-lecture' }, brut(` (et ${String(a.sansMontant)} facture${a.sansMontant > 1 ? 's' : ''} en devise ou sans montant)`)) : null,
    ),
    table(`Avances ${deNom(a.nom)}`, ['Remboursée', 'Date', 'Facture', ['Montant', 'num']], a.pieces.map((p) => lignePiece(p, coches.get(p.id) ?? null))),
    h('div', { classe: 'barre-actions' }, h('label', {}, 'Remboursées le ', le), bouton),
    retour.el,
  );
}

export interface SectionAvances {
  readonly section: Section;
  charger(): Promise<void>;
}

export function sectionAvances(ctx: Contexte): SectionAvances {
  const section = creerSection(6, 'Avances à rembourser', 'Les factures payées de sa poche, par personne');
  const zone = h('div', {});
  section.corps.append(
    h('p', { classe: 'note-lecture' }, 'Une facture sans dépense du SCC (ticket réglé avec une carte personnelle) se marque « Avance de … » sur sa carte « À valider ». Elle se dépose dans le Drive comme les autres, puis attend ici son remboursement.'),
    zone,
  );

  const dessiner = (a: Avances): void => {
    vider(zone);
    const dues = a.aRembourser.reduce((n, x) => n + x.pieces.length, 0);
    section.sousTitre(dues);
    if (a.aRembourser.length === 0) zone.appendChild(h('p', { classe: 'note-lecture' }, 'Aucune avance à rembourser.'));
    for (const x of a.aRembourser) zone.appendChild(blocPersonne(x, ctx, () => void charger()));
    if (a.remboursees.length > 0) {
      zone.appendChild(
        h(
          'details',
          {},
          h('summary', {}, 'Remboursées depuis 90 jours'),
          ...a.remboursees.map((x) =>
            h('div', { classe: 'bloc-avances' }, h('h3', {}, brut(`${x.nom} · ${eurosCentimes(x.total)}`)), table(`Avances remboursées ${deNom(x.nom)}`, ['Remboursée le', 'Date', 'Facture', ['Montant', 'num']], x.pieces.map((p) => lignePiece(p, null)))),
          ),
        ),
      );
    }
  };
  const charger = async (): Promise<void> => {
    dessiner(await lireAvances());
  };
  return { section, charger };
}
